import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Card, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardCharts = ({ dashboardStats, userBusinesses }) => {
  // Generate sample data for charts (replace with real data from API)
  const generateViewsData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const views = months.map(() => Math.floor(Math.random() * 1000) + 100);
    return { months, views };
  };

  const generateBusinessPerformance = () => {
    return userBusinesses?.map(business => ({
      name: (business?.name || business?.businessName || 'Business').length > 15 ? (business?.name || business?.businessName || 'Business').substring(0, 15) + '...' : (business?.name || business?.businessName || 'Business'),
      views: business?.views || business?.view_count || Math.floor(Math.random() * 500) + 50,
      reviews: business?.reviews_count || Math.floor(Math.random() * 20) + 1,
    })) || [];
  };

  const generateCategoryDistribution = () => {
    const categories = {};
    userBusinesses?.forEach(business => {
      const category = business?.category || 'Other';
      categories[category] = (categories[category] || 0) + 1;
    });
    
    return Object.entries(categories).map(([category, count]) => ({
      category,
      count,
      percentage: Math.round((count / (userBusinesses?.length || 1)) * 100)
    }));
  };

  const viewsData = generateViewsData();
  const businessPerformance = generateBusinessPerformance();
  const categoryData = generateCategoryDistribution();

  // Chart configurations
  const viewsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#4a5568',
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      title: {
        display: true,
        text: 'Monthly Views Trend',
        color: '#2d3748',
        font: {
          size: 16,
          weight: '600'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#e2e8f0',
          borderColor: '#e2e8f0'
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          color: '#e2e8f0',
          borderColor: '#e2e8f0'
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11
          }
        }
      }
    },
    elements: {
      line: {
        tension: 0.4
      },
      point: {
        radius: 4,
        hoverRadius: 6
      }
    }
  };

  const viewsChartData = {
    labels: viewsData.months,
    datasets: [
      {
        label: 'Total Views',
        data: viewsData.views,
        borderColor: '#667eea',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      }
    ]
  };

  const businessPerformanceOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#4a5568',
          font: {
            size: 12,
            weight: '500'
          }
        }
      },
      title: {
        display: true,
        text: 'Business Performance',
        color: '#2d3748',
        font: {
          size: 16,
          weight: '600'
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#e2e8f0',
          borderColor: '#e2e8f0'
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11
          }
        }
      },
      x: {
        grid: {
          color: '#e2e8f0',
          borderColor: '#e2e8f0'
        },
        ticks: {
          color: '#718096',
          font: {
            size: 11
          },
          maxRotation: 45
        }
      }
    }
  };

  const businessPerformanceData = {
    labels: businessPerformance.map(b => b.name),
    datasets: [
      {
        label: 'Views',
        data: businessPerformance.map(b => b.views),
        backgroundColor: 'rgba(102, 126, 234, 0.8)',
        borderColor: '#667eea',
        borderWidth: 1
      },
      {
        label: 'Reviews',
        data: businessPerformance.map(b => b.reviews),
        backgroundColor: 'rgba(237, 137, 54, 0.8)',
        borderColor: '#ed8936',
        borderWidth: 1
      }
    ]
  };

  const categoryOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#4a5568',
          font: {
            size: 12,
            weight: '500'
          },
          padding: 20
        }
      },
      title: {
        display: true,
        text: 'Business Categories',
        color: '#2d3748',
        font: {
          size: 16,
          weight: '600'
        }
      }
    }
  };

  const categoryChartData = {
    labels: categoryData.map(c => c.category),
    datasets: [
      {
        data: categoryData.map(c => c.count),
        backgroundColor: [
          '#667eea',
          '#764ba2',
          '#ed8936',
          '#48bb78',
          '#38b2ac',
          '#9f7aea',
          '#f093fb',
          '#fbb6ce'
        ],
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 4
      }
    ]
  };

  return (
    <Row className="mb-4">
      {/* Views Trend Chart */}
      <Col lg={8} className="mb-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Card className="chart-card h-100">
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Line data={viewsChartData} options={viewsChartOptions} />
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Col>

      {/* Category Distribution */}
      <Col lg={4} className="mb-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="chart-card h-100">
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Doughnut data={categoryChartData} options={categoryOptions} />
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Col>

      {/* Business Performance Chart */}
      <Col lg={12} className="mb-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="chart-card">
            <Card.Body>
              <div style={{ height: '400px' }}>
                <Bar data={businessPerformanceData} options={businessPerformanceOptions} />
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Col>
    </Row>
  );
};

export default DashboardCharts;
